import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, FileText, Loader2, Upload, X, XCircle } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ImportResult, ReviewRow } from './import-candidates.utils';
import { parsePhone, validateAll, validateRow } from './import-candidates.utils';
import { StarRatingInput } from './star-rating';

import { Button } from '~/components/ui/button';
import { Checkbox } from '~/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '~/components/ui/dialog';
import { Input } from '~/components/ui/input';
import { i18n } from '~/lib/i18n';

// ── Constants ────────────────────────────────────────────────────────────────

const CV_MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const CV_ACCEPTED_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/png',
  'image/webp'
] as const;

// ── Types ────────────────────────────────────────────────────────────────────

type FileStatus = 'pending' | 'parsing' | 'done' | 'error';

type FileEntry = {
  id: string;
  file: File;
  blobUrl: string;
  status: FileStatus;
  parseError: string | null;
  row: ReviewRow;
  rating: number | null;
  notes: string;
};

type Step = 'upload' | 'confirm' | 'success';

// ── Helpers ──────────────────────────────────────────────────────────────────

function emptyRow(): ReviewRow {
  return {
    name: '',
    email: '',
    phone: '',
    nameError: null,
    emailError: null,
    phoneError: null,
    parsedPhoneNumber: null,
    parsedPhoneCountryCode: null
  };
}

function makeEntry(file: File): FileEntry {
  return {
    id: `${String(Date.now())}-${String(Math.random())}`,
    file,
    blobUrl: URL.createObjectURL(file),
    status: 'pending',
    parseError: null,
    row: emptyRow(),
    rating: null,
    notes: ''
  };
}

function isAcceptedType(type: string): boolean {
  return (CV_ACCEPTED_TYPES as readonly string[]).includes(type);
}

// ── Props ────────────────────────────────────────────────────────────────────

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: string;
  deptId: string;
  jobId: string;
};

// ── Component ────────────────────────────────────────────────────────────────

export function ImportCandidatesFromCvDialog({
  open,
  onOpenChange,
  orgId,
  deptId,
  jobId
}: Props): React.ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>('upload');
  const [entries, setEntries] = useState<FileEntry[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [skipInvalid, setSkipInvalid] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const processingRef = useRef(false);

  const selectedEntry = entries.find((e) => e.id === selectedId) ?? null;

  function resetState(): void {
    entries.forEach((e) => {
      URL.revokeObjectURL(e.blobUrl);
    });
    setStep('upload');
    setEntries([]);
    setSelectedId(null);
    setSkipInvalid(false);
    setResult(null);
    setIsDragOver(false);
    processingRef.current = false;
  }

  // ── File queue processor ──────────────────────────────────────────────────

  useEffect(() => {
    if (processingRef.current) return;
    const pending = entries.find((e) => e.status === 'pending');
    if (!pending) return;

    processingRef.current = true;

    setEntries((prev) => prev.map((e) => (e.id === pending.id ? { ...e, status: 'parsing' } : e)));

    void (async () => {
      try {
        const formData = new FormData();
        formData.append('file', pending.file);
        const res = await fetch(
          `/api/org/${orgId}/department/${deptId}/jobs/${jobId}/applications/parse-cv`,
          { method: 'POST', body: formData }
        );

        if (!res.ok) {
          const json = (await res.json()) as { error?: string };
          throw new Error(json.error ?? t('applications.importFromCvDialog.status.error'));
        }

        const data = (await res.json()) as {
          name: string | null;
          email: string | null;
          phone: string | null;
        };

        const rawRow: ReviewRow = {
          name: data.name ?? '',
          email: data.email ?? '',
          phone: data.phone ?? '',
          nameError: null,
          emailError: null,
          phoneError: null,
          parsedPhoneNumber: null,
          parsedPhoneCountryCode: null
        };
        const validatedRow = validateRow(rawRow);

        setEntries((prev) =>
          prev.map((e) => (e.id === pending.id ? { ...e, status: 'done', row: validatedRow } : e))
        );
        setSelectedId((cur) => cur ?? pending.id);
      } catch (err) {
        setEntries((prev) =>
          prev.map((e) =>
            e.id === pending.id
              ? {
                  ...e,
                  status: 'error',
                  parseError:
                    err instanceof Error
                      ? err.message
                      : t('applications.importFromCvDialog.status.error')
                }
              : e
          )
        );
        setSelectedId((cur) => cur ?? pending.id);
      } finally {
        processingRef.current = false;
      }
    })();
  }, [entries, orgId, deptId, jobId, t]);

  // ── File add handler ──────────────────────────────────────────────────────

  const addFiles = useCallback((files: FileList | File[]) => {
    const arr = Array.from(files);
    const newEntries: FileEntry[] = [];

    for (const file of arr) {
      if (!isAcceptedType(file.type)) continue;
      if (file.size > CV_MAX_FILE_SIZE) {
        newEntries.push({
          id: `${String(Date.now())}-${String(Math.random())}`,
          file,
          blobUrl: URL.createObjectURL(file),
          status: 'error',
          parseError: i18n.t('applications.importFromCvDialog.errors.fileTooLarge'),
          row: emptyRow(),
          rating: null,
          notes: ''
        });
        continue;
      }
      newEntries.push(makeEntry(file));
    }

    if (newEntries.length === 0) return;
    setEntries((prev) => [...prev, ...newEntries]);
    setSelectedId((cur) => cur ?? newEntries[0]?.id ?? null);
  }, []);

  // ── Remove entry ──────────────────────────────────────────────────────────

  function removeEntry(id: string): void {
    setEntries((prev) => {
      const idx = prev.findIndex((e) => e.id === id);
      const next = prev.filter((e) => e.id !== id);
      URL.revokeObjectURL(prev[idx]?.blobUrl ?? '');

      if (selectedId === id) {
        const nextEntry = next[idx] ?? next[idx - 1];
        setSelectedId(nextEntry?.id ?? null);
      }
      return next;
    });
  }

  // ── Row field update ──────────────────────────────────────────────────────

  function updateRowField(id: string, field: 'name' | 'email' | 'phone', value: string): void {
    setEntries((prev) =>
      prev.map((e) => {
        if (e.id !== id) return e;
        const updated = { ...e.row, [field]: value };
        return { ...e, row: updated };
      })
    );
  }

  // ── Drag and drop ─────────────────────────────────────────────────────────

  function handleDragOver(ev: React.DragEvent): void {
    ev.preventDefault();
    setIsDragOver(true);
  }

  function handleDragLeave(): void {
    setIsDragOver(false);
  }

  function handleDrop(ev: React.DragEvent): void {
    ev.preventDefault();
    setIsDragOver(false);
    if (ev.dataTransfer.files.length > 0) addFiles(ev.dataTransfer.files);
  }

  // ── Import mutation ───────────────────────────────────────────────────────

  const importMutation = useMutation({
    mutationFn: async (toImport: FileEntry[]) => {
      // Step 1: create applications via JSON bulk endpoint
      const bulkRes = await fetch(
        `/api/org/${orgId}/department/${deptId}/jobs/${jobId}/applications/bulk`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            candidates: toImport.map((e) => ({
              name: e.row.name,
              email: e.row.email,
              phoneNumber: e.row.parsedPhoneNumber ?? '',
              phoneCountryCode: e.row.parsedPhoneCountryCode ?? '',
              ...(e.rating != null && { rating: e.rating }),
              ...(e.notes.trim() && { notes: e.notes.trim() })
            }))
          })
        }
      );
      if (!bulkRes.ok) throw new Error(t('applications.importFromCvDialog.status.error'));
      const bulkData = (await bulkRes.json()) as {
        results: Array<{ email: string; status: string; candidateId?: string }>;
      };

      // Step 2: upload CVs for created candidates only
      type CreatedEntry = { entry: FileEntry; candidateId: string };
      const createdEntries: CreatedEntry[] = toImport.flatMap((e, i) => {
        const r = bulkData.results[i];
        return r?.status === 'created' && r.candidateId
          ? [{ entry: e, candidateId: r.candidateId }]
          : [];
      });

      if (createdEntries.length > 0) {
        const formData = new FormData();
        const cvCandidates = createdEntries.map((x) => ({ candidateId: x.candidateId }));
        formData.append('candidates', JSON.stringify(cvCandidates));
        createdEntries.forEach((x, i) => {
          formData.append(`file_${String(i)}`, x.entry.file);
        });

        await fetch(`/api/org/${orgId}/candidates/cv/bulk`, { method: 'POST', body: formData });
      }

      return bulkData;
    },
    onSuccess: (data) => {
      const r = data.results;
      setResult({
        created: r.filter((x) => x.status === 'created').length,
        conflict: r.filter((x) => x.status === 'conflict').length,
        error: r.filter((x) => x.status === 'error').length
      });
      setStep('success');
      void queryClient.invalidateQueries({ queryKey: ['applications', orgId, deptId, jobId] });
      void queryClient.invalidateQueries({ queryKey: ['jobs', orgId, deptId] });
    }
  });

  // ── Confirm handler ───────────────────────────────────────────────────────

  function handleReviewAndImport(): void {
    // Re-validate all rows and run duplicate detection across entries
    const allRows = entries.map((e) => e.row);
    const validated = validateAll(allRows);
    setEntries((prev) => prev.map((e, i) => ({ ...e, row: validated[i] ?? e.row })));
    setSkipInvalid(false);
    setStep('confirm');
  }

  function handleImport(): void {
    const revalidated = validateAll(entries.map((e) => e.row));
    const updatedEntries = entries.map((e, i) => ({ ...e, row: revalidated[i] ?? e.row }));
    setEntries(updatedEntries);

    const toImport = updatedEntries.filter(
      (e) => e.status !== 'error' && !e.row.nameError && !e.row.emailError && !e.row.phoneError
    );

    if (toImport.length === 0) return;
    importMutation.mutate(toImport);
  }

  // ── Derived values ────────────────────────────────────────────────────────

  const doneEntries = entries.filter((e) => e.status === 'done' || e.status === 'error');
  const validEntries = entries.filter(
    (e) => e.status === 'done' && !e.row.nameError && !e.row.emailError && !e.row.phoneError
  );
  const invalidEntries = doneEntries.filter(
    (e) => e.status === 'error' || e.row.nameError || e.row.emailError || e.row.phoneError
  );

  const canReview = validEntries.length > 0;
  const allValid = invalidEntries.length === 0 && validEntries.length > 0;
  const canImport = allValid || (skipInvalid && validEntries.length > 0);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) resetState();
        onOpenChange(v);
      }}
    >
      <DialogContent
        className="sm:max-w-6xl"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <DialogHeader>
          <DialogTitle>{t('applications.importFromCvDialog.title')}</DialogTitle>
        </DialogHeader>

        {/* ── Step: upload ─────────────────────────────────────────────────── */}

        {step === 'upload' && entries.length === 0 && (
          <div className="flex flex-col items-center gap-4 py-6">
            <div
              className={`flex w-full flex-col items-center gap-3 rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
                isDragOver ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
              }`}
            >
              <Upload className="size-8 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">
                  {t('applications.importFromCvDialog.upload.hint')}
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {t('applications.importFromCvDialog.upload.supportedFormats')}
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
                {t('applications.importFromCvDialog.upload.browse')}
              </Button>
            </div>
            <input
              ref={fileRef}
              type="file"
              multiple
              accept=".pdf,.docx,.jpg,.jpeg,.png,.webp"
              className="hidden"
              onChange={(e) => {
                if (e.target.files && e.target.files.length > 0) addFiles(e.target.files);
                e.target.value = '';
              }}
            />
          </div>
        )}

        {step === 'upload' && entries.length > 0 && (
          <>
            <div
              className={`flex min-h-[700px] overflow-hidden rounded-lg border border-border transition-colors ${
                isDragOver ? 'border-primary bg-primary/5' : ''
              }`}
            >
              {/* Left: file tab list */}
              <div className="flex w-52 shrink-0 flex-col border-r border-border">
                <div className="flex-1 overflow-y-auto">
                  {entries.map((entry) => (
                    <button
                      key={entry.id}
                      type="button"
                      onClick={() => {
                        setSelectedId(entry.id);
                      }}
                      className={`group flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm transition-colors ${
                        selectedId === entry.id ? 'bg-muted font-medium' : 'hover:bg-muted/50'
                      }`}
                    >
                      <span className="shrink-0">
                        {entry.status === 'parsing' && (
                          <Loader2 className="size-3.5 animate-spin text-muted-foreground" />
                        )}
                        {entry.status === 'pending' && (
                          <span className="size-3.5 text-muted-foreground">·</span>
                        )}
                        {entry.status === 'done' &&
                          !entry.row.nameError &&
                          !entry.row.emailError &&
                          !entry.row.phoneError && (
                            <CheckCircle2 className="size-3.5 text-green-600" />
                          )}
                        {(entry.status === 'error' ||
                          entry.row.nameError ||
                          entry.row.emailError ||
                          entry.row.phoneError) &&
                          entry.status !== 'pending' &&
                          entry.status !== 'parsing' && (
                            <XCircle className="size-3.5 text-destructive" />
                          )}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-xs">{entry.file.name}</span>
                      <button
                        type="button"
                        onClick={(ev) => {
                          ev.stopPropagation();
                          removeEntry(entry.id);
                        }}
                        className="ml-auto shrink-0 rounded p-0.5 opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"
                        aria-label="Remove"
                      >
                        <X className="size-3.5" />
                      </button>
                    </button>
                  ))}
                </div>
                <div
                  role="button"
                  tabIndex={0}
                  className={`flex cursor-pointer flex-col items-center gap-1.5 border-t border-dashed border-border p-4 transition-colors ${
                    isDragOver
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:border-primary/50 hover:bg-muted/50'
                  }`}
                  onClick={() => fileRef.current?.click()}
                  onKeyDown={(ev) => {
                    if (ev.key === 'Enter' || ev.key === ' ') fileRef.current?.click();
                  }}
                  onDragOver={(ev) => {
                    ev.preventDefault();
                    ev.stopPropagation();
                    setIsDragOver(true);
                  }}
                  onDragLeave={(ev) => {
                    ev.stopPropagation();
                    setIsDragOver(false);
                  }}
                  onDrop={(ev) => {
                    ev.preventDefault();
                    ev.stopPropagation();
                    setIsDragOver(false);
                    if (ev.dataTransfer.files.length > 0) addFiles(ev.dataTransfer.files);
                  }}
                >
                  <Upload className="size-4" />
                  <span className="text-center text-xs font-medium">
                    {t('applications.importFromCvDialog.upload.addMore')}
                  </span>
                  <span className="text-center text-xs opacity-60">
                    {t('applications.importFromCvDialog.upload.dragOrClick')}
                  </span>
                </div>
              </div>

              {/* Right: editable fields + CV preview side by side */}
              <div className="flex flex-1 overflow-hidden">
                {selectedEntry ? (
                  <>
                    {/* Editable fields */}
                    <div className="flex w-56 shrink-0 flex-col overflow-y-auto border-r border-border p-4">
                      {selectedEntry.status === 'parsing' && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Loader2 className="size-4 animate-spin" />
                          {t('applications.importFromCvDialog.status.parsing')}
                        </div>
                      )}
                      {selectedEntry.status === 'error' && selectedEntry.parseError && (
                        <p className="text-sm text-destructive">{selectedEntry.parseError}</p>
                      )}
                      {(selectedEntry.status === 'done' ||
                        (selectedEntry.status === 'error' && !selectedEntry.parseError)) && (
                        <div className="flex flex-col gap-3">
                          <div className="flex flex-col gap-1">
                            <label className="text-xs font-medium text-muted-foreground">
                              {t('applications.importFromCvDialog.fields.name')}
                            </label>
                            <Input
                              value={selectedEntry.row.name}
                              onChange={(e) => {
                                updateRowField(selectedEntry.id, 'name', e.target.value);
                              }}
                              onBlur={() => {
                                setEntries((prev) =>
                                  prev.map((e) =>
                                    e.id === selectedEntry.id
                                      ? { ...e, row: validateRow(e.row) }
                                      : e
                                  )
                                );
                              }}
                            />
                            {selectedEntry.row.nameError && (
                              <p className="text-xs text-destructive">
                                {selectedEntry.row.nameError}
                              </p>
                            )}
                          </div>
                          <div className="flex flex-col gap-1">
                            <label className="text-xs font-medium text-muted-foreground">
                              {t('applications.importFromCvDialog.fields.email')}
                            </label>
                            <Input
                              type="email"
                              value={selectedEntry.row.email}
                              onChange={(e) => {
                                updateRowField(selectedEntry.id, 'email', e.target.value);
                              }}
                              onBlur={() => {
                                setEntries((prev) =>
                                  prev.map((e) =>
                                    e.id === selectedEntry.id
                                      ? { ...e, row: validateRow(e.row) }
                                      : e
                                  )
                                );
                              }}
                            />
                            {selectedEntry.row.emailError && (
                              <p className="text-xs text-destructive">
                                {selectedEntry.row.emailError}
                              </p>
                            )}
                          </div>
                          <div className="flex flex-col gap-1">
                            <label className="text-xs font-medium text-muted-foreground">
                              {t('applications.importFromCvDialog.fields.phone')}
                            </label>
                            <Input
                              value={selectedEntry.row.phone}
                              onChange={(e) => {
                                const v = e.target.value;
                                const { parsedPhoneNumber, parsedPhoneCountryCode, phoneError } =
                                  parsePhone(v);
                                setEntries((prev) =>
                                  prev.map((e) =>
                                    e.id === selectedEntry.id
                                      ? {
                                          ...e,
                                          row: {
                                            ...e.row,
                                            phone: v,
                                            parsedPhoneNumber,
                                            parsedPhoneCountryCode,
                                            phoneError
                                          }
                                        }
                                      : e
                                  )
                                );
                              }}
                            />
                            {selectedEntry.row.phoneError && (
                              <p className="text-xs text-destructive">
                                {selectedEntry.row.phoneError}
                              </p>
                            )}
                          </div>
                          <div className="flex flex-col gap-1">
                            <label className="text-xs font-medium text-muted-foreground">
                              {t('applications.importFromCvDialog.fields.rating')}
                            </label>
                            <StarRatingInput
                              value={selectedEntry.rating}
                              onChange={(v) => {
                                setEntries((prev) =>
                                  prev.map((e) =>
                                    e.id === selectedEntry.id ? { ...e, rating: v } : e
                                  )
                                );
                              }}
                            />
                          </div>
                          <div className="flex flex-col gap-1">
                            <label className="text-xs font-medium text-muted-foreground">
                              {t('applications.importFromCvDialog.fields.notes')}
                            </label>
                            <textarea
                              value={selectedEntry.notes}
                              onChange={(e) => {
                                const v = e.target.value;
                                setEntries((prev) =>
                                  prev.map((en) =>
                                    en.id === selectedEntry.id ? { ...en, notes: v } : en
                                  )
                                );
                              }}
                              placeholder={t(
                                'applications.importFromCvDialog.fields.notesPlaceholder'
                              )}
                              rows={3}
                              maxLength={1000}
                              className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* CV preview */}
                    <div className="min-h-0 flex-1 bg-muted/30">
                      {selectedEntry.file.type === 'application/pdf' ? (
                        <iframe
                          src={selectedEntry.blobUrl}
                          className="h-full w-full"
                          title={selectedEntry.file.name}
                        />
                      ) : selectedEntry.file.type.startsWith('image/') ? (
                        <div className="flex h-full items-center justify-center p-4">
                          <img
                            src={selectedEntry.blobUrl}
                            alt={selectedEntry.file.name}
                            className="max-h-full max-w-full object-contain"
                          />
                        </div>
                      ) : (
                        <div className="flex h-full flex-col items-center justify-center gap-2 text-muted-foreground">
                          <FileText className="size-10" />
                          <p className="text-xs">
                            {t('applications.importFromCvDialog.preview.noPreview')}
                          </p>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                    {t('applications.importFromCvDialog.upload.hint')}
                  </div>
                )}
              </div>
            </div>

            <input
              ref={fileRef}
              type="file"
              multiple
              accept=".pdf,.docx,.jpg,.jpeg,.png,.webp"
              className="hidden"
              onChange={(e) => {
                if (e.target.files && e.target.files.length > 0) addFiles(e.target.files);
                e.target.value = '';
              }}
            />

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  resetState();
                  onOpenChange(false);
                }}
              >
                {t('applications.importFromCvDialog.actions.cancel')}
              </Button>
              <Button onClick={handleReviewAndImport} disabled={!canReview}>
                {t('applications.importFromCvDialog.actions.reviewAndImport')}
              </Button>
            </DialogFooter>
          </>
        )}

        {/* ── Step: confirm ────────────────────────────────────────────────── */}

        {step === 'confirm' && (
          <>
            <div className="flex flex-col gap-3 py-2">
              <p className="text-sm text-muted-foreground">
                {allValid
                  ? t('applications.importFromCvDialog.confirm.allValid', {
                      count: validEntries.length
                    })
                  : t('applications.importFromCvDialog.confirm.hasErrors', {
                      valid: validEntries.length,
                      invalid: invalidEntries.length
                    })}
              </p>

              <div className="max-h-64 overflow-y-auto rounded border border-border text-xs">
                <table className="w-full">
                  <thead className="sticky top-0 bg-muted">
                    <tr>
                      <th className="px-2 py-1.5 text-left font-medium">
                        {t('applications.importFromCvDialog.fields.name')}
                      </th>
                      <th className="px-2 py-1.5 text-left font-medium">
                        {t('applications.importFromCvDialog.fields.email')}
                      </th>
                      <th className="px-2 py-1.5 text-left font-medium">
                        {t('applications.importFromCvDialog.fields.phone')}
                      </th>
                      <th className="px-2 py-1.5 text-left font-medium">
                        {t('applications.importFromCvDialog.review.colStatus')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((entry) => {
                      const hasError =
                        entry.status === 'error' ||
                        entry.row.nameError ||
                        entry.row.emailError ||
                        entry.row.phoneError;
                      const errorMsg =
                        entry.parseError ??
                        entry.row.nameError ??
                        entry.row.emailError ??
                        entry.row.phoneError;
                      return (
                        <tr
                          key={entry.id}
                          className={hasError ? 'bg-destructive/5' : 'even:bg-muted/20'}
                        >
                          <td className="px-2 py-1.5">{entry.row.name || '—'}</td>
                          <td className="px-2 py-1.5">{entry.row.email || '—'}</td>
                          <td className="px-2 py-1.5">{entry.row.phone || '—'}</td>
                          <td className="px-2 py-1.5">
                            {hasError ? (
                              <span className="flex items-center gap-1 text-destructive">
                                <XCircle className="size-3.5 shrink-0" />
                                {errorMsg}
                              </span>
                            ) : (
                              <CheckCircle2 className="size-3.5 text-green-600" />
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {!allValid && validEntries.length > 0 && (
                <label className="flex cursor-pointer items-center gap-2 text-sm">
                  <Checkbox
                    checked={skipInvalid}
                    onCheckedChange={(v) => {
                      setSkipInvalid(v);
                    }}
                  />
                  {t('applications.importFromCvDialog.confirm.skipInvalid', {
                    count: validEntries.length
                  })}
                </label>
              )}

              {importMutation.isError && (
                <p className="text-xs text-destructive">{importMutation.error.message}</p>
              )}
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setStep('upload');
                }}
              >
                {t('applications.importFromCvDialog.actions.back')}
              </Button>
              <Button onClick={handleImport} disabled={!canImport || importMutation.isPending}>
                {t('applications.importFromCvDialog.actions.import')}
              </Button>
            </DialogFooter>
          </>
        )}

        {/* ── Step: success ────────────────────────────────────────────────── */}

        {step === 'success' && result && (
          <>
            <div className="flex flex-col items-center gap-6 py-8">
              <div className="flex size-14 items-center justify-center rounded-full bg-green-100">
                <CheckCircle2 className="size-8 text-green-600" />
              </div>
              <div className="flex flex-col items-center gap-1 text-center">
                <p className="text-lg font-semibold">
                  {t('applications.importFromCvDialog.success.title')}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t('applications.importFromCvDialog.success.subtitle', {
                    count: result.created
                  })}
                </p>
              </div>
              <div className="flex w-full justify-center gap-6">
                <div className="flex flex-col items-center gap-0.5">
                  <span className="text-2xl font-bold text-green-600">{result.created}</span>
                  <span className="text-xs text-muted-foreground">
                    {t('applications.importFromCvDialog.success.created')}
                  </span>
                </div>
                {result.conflict > 0 && (
                  <div className="flex flex-col items-center gap-0.5">
                    <span className="text-2xl font-bold text-amber-500">{result.conflict}</span>
                    <span className="text-xs text-muted-foreground">
                      {t('applications.importFromCvDialog.success.conflict')}
                    </span>
                  </div>
                )}
                {result.error > 0 && (
                  <div className="flex flex-col items-center gap-0.5">
                    <span className="text-2xl font-bold text-destructive">{result.error}</span>
                    <span className="text-xs text-muted-foreground">
                      {t('applications.importFromCvDialog.success.error')}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button
                onClick={() => {
                  resetState();
                  onOpenChange(false);
                }}
              >
                {t('applications.importFromCvDialog.actions.done')}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
